import styled from "@emotion/styled"
import type { MarketplaceKey } from "@porsche-customer/slfinrtl-context-types"
import { PInputSearch } from "@porsche-design-system/components-react"
import type { InputSearchInputEventDetail } from "@porsche-design-system/components-react"
import { getMediaQueryMin, spacingFluidSm } from "@porsche-design-system/components-react/emotion"
import type { DealershipListOptions } from "@slfinrtl/ui-bff-routes"
import debounce from "lodash-es/debounce"
import { useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { MultiSelect, Select } from "src/components"

const messages = defineMessages({
	marketplace: {
		defaultMessage: "Marketplace",
		id: "dealerList.filters.marketplace",
	},
	ppnOrganizationType: {
		defaultMessage: "PPN Organization Type",
		id: "dealerList.filters.ppnOrganizationType",
	},
	search: {
		defaultMessage: "Search",
		id: "dealerList.filters.search",
	},
})

export interface Filters {
	marketplace?: MarketplaceKey | null
	organisationFunctions: string[]
	searchText: string | undefined
}

interface DealershipFiltersProps {
	filterOptions: DealershipListOptions
	updateFilter: (filter: Filters) => void
}

export const DealershipFilters = ({ filterOptions, updateFilter }: DealershipFiltersProps) => {
	const { formatMessage } = useIntl()
	const marketplaceFilterOptions = filterOptions.filter.marketplaces.options ?? []
	const organisationFilterOptions = filterOptions.filter.organisationFunctions.options ?? []

	const [marketplaceFilter, setMarketplaceFilter] = useState<MarketplaceKey | null>()
	const [organisationFilters, setOrganisationFilters] = useState<string[]>([])
	const [searchText, setSearchText] = useState("")

	const debouncedSearchOnChange = debounce((event: CustomEvent<InputSearchInputEventDetail>) => {
		setSearchText((event.target as HTMLInputElement).value)
		updateFilter({
			marketplace: marketplaceFilter,
			organisationFunctions: organisationFilters,
			searchText: (event.target as HTMLInputElement).value,
		})
	}, 300)

	return (
		<FilterWrapper>
			<PInputSearch
				name="search"
				label={formatMessage(messages.search)}
				clear
				indicator
				onInput={debouncedSearchOnChange}
			/>
			<Select
				name="marketplace"
				label={formatMessage(messages.marketplace)}
				value={marketplaceFilter}
				options={marketplaceFilterOptions}
				filter
				onChange={(value) => {
					setMarketplaceFilter(value)
					updateFilter({
						marketplace: value,
						organisationFunctions: organisationFilters,
						searchText,
					})
				}}
			/>
			<MultiSelect
				name="organisationFunctions"
				label={formatMessage(messages.ppnOrganizationType)}
				value={organisationFilters}
				options={organisationFilterOptions.map((item) => ({
					label: item.label,
					value: item.value,
				}))}
				onChange={(value) => {
					setOrganisationFilters(value)
					updateFilter({
						marketplace: marketplaceFilter,
						organisationFunctions: value,
						searchText,
					})
				}}
			/>
		</FilterWrapper>
	)
}

const FilterWrapper = styled.div`
	display: grid;
	gap: ${spacingFluidSm};

	${getMediaQueryMin("s")} {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	${getMediaQueryMin("l")} {
		grid-template-columns: repeat(6, minmax(0, 1fr));
	}
`
